import { useEffect, useMemo, useState } from 'react';
import { Icon } from '../../icons';
import { Badge, Btn, Card, Alert, DataTable, SectionHeader } from '../../components/ui';
import InteractiveBarChart from './InteractiveBarChart';
import DonutChart from './DonutChart';
import TrustScoreWidget from './TrustScoreWidget';
import { getDashboard } from '../../api/dashboard';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';

const translations = { en, hi, gu };

const C = {
  red: { g: 'linear-gradient(135deg,#dc2626,#f87171)', soft: 'rgba(239,68,68,0.09)', glow: 'rgba(239,68,68,0.22)', col: '#dc2626', bdr: '#fecaca', bg: 'var(--color-surface)', twText: 'text-red-700 dark:text-red-400' },
  green: { g: 'linear-gradient(135deg,#059669,#34d399)', soft: 'rgba(16,185,129,0.09)', glow: 'rgba(16,185,129,0.22)', col: '#059669', bdr: '#a7f3d0', bg: 'var(--color-surface)', twText: 'text-emerald-700 dark:text-emerald-400' },
  amber: { g: 'linear-gradient(135deg,#d97706,#fbbf24)', soft: 'rgba(245,158,11,0.09)', glow: 'rgba(245,158,11,0.22)', col: '#d97706', bdr: '#fde68a', bg: 'var(--color-surface)', twText: 'text-amber-700 dark:text-amber-400' },
  blue: { g: 'linear-gradient(135deg,#4f46e5,#818cf8)', soft: 'rgba(79,70,229,0.09)', glow: 'rgba(79,70,229,0.22)', col: '#4f46e5', bdr: '#c4b5fd', bg: 'var(--color-surface)', twText: 'text-indigo-700 dark:text-indigo-400' },
};

function formatCompactRupees(value) {
  const amount = Number(value || 0);
  const prefix = amount < 0 ? '-' : '';
  const absolute = Math.abs(amount);

  if (absolute >= 10000000) {
    return `${prefix}₹${(absolute / 10000000).toFixed(2).replace(/\.?0+$/, '')}Cr`;
  }

  if (absolute >= 100000) {
    return `${prefix}₹${(absolute / 100000).toFixed(2).replace(/\.?0+$/, '')}L`;
  }

  if (absolute >= 1000) {
    return `${prefix}₹${(absolute / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  }

  return `${prefix}₹${absolute.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function DashStat({ label, value, note, delta, deltaUp, icon, accent, progress, progressLabel, chips, onClick }) {
  const [hov, setHov] = useState(false);
  const ac = C[accent] || C.blue;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      className="bg-white dark:bg-slate-900/40 backdrop-blur-md border border-slate-100 dark:border-slate-800 transition-all duration-300"
      style={{
        position: 'relative',
        borderRadius: 20,
        borderWidth: 1.5,
        borderColor: hov ? ac.col : undefined,
        boxShadow: hov
          ? `0 5px 15px ${ac.glow}, 0 4px 5px rgba(0,0,0,0.06)`
          : '0 1px 4px rgba(0,0,0,0.04), 0 6px 10px rgba(0,0,0,0.04)',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        transform: hov && onClick ? 'translateY(-5px) scale(1.016)' : 'none',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ height: 3.5, background: ac.g, flexShrink: 0 }} className="opacity-100 dark:opacity-60" />
      <div
        style={{
          position: 'absolute', top: 3.5, left: 0, right: 0, height: 120,
          background: `radial-gradient(ellipse 90% 70% at 40% 0%, ${ac.soft}, transparent 70%)`,
          opacity: hov ? 1 : 0,
          transition: 'opacity .3s',
          pointerEvents: 'none',
        }}
      />

      <div style={{ padding: '16px 18px 4px', flex: 1, position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 50, height: 50, borderRadius: 15, background: ac.soft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <div
                style={{
                  width: 32, height: 32, borderRadius: 10, background: ac.g,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: `0 4px 14px ${ac.glow}`,
                  transform: hov ? 'rotate(-12deg) scale(1.18)' : 'none',
                  transition: 'transform .35s cubic-bezier(.34,1.56,.64,1)',
                }}
              >
                {icon && <Icon name={icon} size={15} color="white" strokeWidth={2.2} />}
              </div>
            </div>

            <div
              className={hov ? ac.twText : 'text-ink dark:text-white'}
              style={{ fontSize: 26, fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1, transition: 'color .22s', whiteSpace: 'nowrap' }}
            >
              {value}
            </div>
          </div>

          {delta && (
            <div
              className={`flex items-center gap-[3px] text-[11px] sm:text-xs font-bold px-[8px] py-[3px] rounded-full border tracking-[.01em] shrink-0 whitespace-nowrap ${deltaUp
                ? 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30 dark:text-emerald-400 dark:border-emerald-500/30'
                : 'bg-red-500/15 text-red-700 border-red-500/30 dark:text-red-400 dark:border-red-500/30'
                }`}
            >
              <Icon name={deltaUp ? 'trend_up' : 'trend_down'} size={9} color="currentColor" />
              {delta}
            </div>
          )}
        </div>

        <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }} className="text-slate-500 dark:text-slate-300 uppercase tracking-[.07em]">
          {label}
        </div>

        {note && <div style={{ fontSize: 11, marginTop: 3 }} className="text-slate-500 dark:text-slate-300">{note}</div>}

        {chips && (
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 10 }}>
            {chips.map((chip, index) => (
              <div
                key={index}
                className={ac.twText}
                style={{
                  fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 7,
                  background: ac.soft, whiteSpace: 'nowrap',
                }}
              >
                {chip}
              </div>
            ))}
          </div>
        )}
      </div>

      {progress !== undefined && (
        <div style={{ padding: '6px 18px 6px', position: 'relative' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
            <span style={{ fontSize: 10, fontWeight: 600 }} className="text-slate-500 dark:text-slate-300">{progressLabel}</span>
            <span style={{ fontSize: 11, fontWeight: 800, color: ac.col }}>{progress}%</span>
          </div>
          <div className="h-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div
              style={{
                height: '100%', borderRadius: 99, background: ac.g,
                width: `${progress}%`,
                transition: 'width 1.3s cubic-bezier(.34,1.56,.64,1) .25s',
                boxShadow: hov ? `0 0 10px ${ac.glow}` : 'none',
                transitionProperty: 'width, box-shadow',
              }}
            />
          </div>
        </div>
      )}

      {onClick && (
        <div style={{ padding: '0 18px 14px', display: 'flex', justifyContent: 'flex-end' }}>
          <div
            className={ac.twText}
            style={{
              fontSize: 10, fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: 2,
              opacity: hov ? 1 : 0,
              transform: hov ? 'translateX(0)' : 'translateX(4px)',
              transition: 'opacity .2s, transform .2s',
            }}
          >
            View <Icon name="chevron_right" size={10} color="currentColor" />
          </div>
        </div>
      )}
      {!onClick && <div style={{ height: 14 }} />}
    </div>
  );
}

export default function DashboardScreen({ onNav }) {
  const { user } = useAuth();
  const { language } = useSettings();
  const t = (key) => translations[language]?.dashboard?.[key] || translations.en.dashboard?.[key] || key;

  const isAdmin = user?.roles_list?.some(r => ['super_admin', 'admin'].includes(r));
  const isMember = (user?.primary_role_label === 'Member' || user?.roles_list?.includes('member')) && !isAdmin;
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dismissAlert, setDismissAlert] = useState(false);

  useEffect(() => {
    let active = true;

    const loadDashboard = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await getDashboard();
        if (!active) {
          return;
        }
        setDashboard(response?.data || response);
      } catch (err) {
        if (!active) {
          return;
        }
        setError(err?.message || 'Failed to load dashboard.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      active = false;
    };
  }, []);

  const stats = dashboard?.cards || {};
  const charts = dashboard?.charts || {};
  const recentPayments = dashboard?.recent_payments || [];
  const quickStats = dashboard?.quick_stats || [];
  const trustScore = dashboard?.trust_score || null;
  const alert = dashboard?.alert;

  const barData = useMemo(() => (charts.collection_vs_target?.series || []).map((point) => [point[0], point[1]]), [charts.collection_vs_target]);
  const barLabels = useMemo(() => charts.collection_vs_target?.labels || [], [charts.collection_vs_target]);
  const getExpenseLabel = (label) => {
    switch (label) {
      case 'Vendors/AMC': return t('expense_vendors');
      case 'Petty Cash': return t('expense_petty_cash');
      case 'Salaries': return t('expense_salaries');
      case 'Festival': return t('expense_festival');
      case 'Utilities': return t('expense_utilities');
      default: return label;
    }
  };

  const donutSegs = useMemo(() => (charts.expense_breakdown?.segments || []).map((segment) => ({
    label: getExpenseLabel(segment.label),
    v: segment.v,
    color: segment.color,
    hoverColor: segment.hoverColor,
  })), [charts.expense_breakdown, language]);

  const handleExportCollectionChart = () => {
    if (!barData.length || !barLabels.length) {
      return;
    }

    const rows = [
      ['Month', 'Collected', 'Target', 'Gap', 'Collection Rate'],
      ...barData.map((pair, index) => {
        const collected = Number(pair?.[0] || 0);
        const target = Number(pair?.[1] || 0);
        const gap = Math.max(target - collected, 0);
        const rate = target > 0 ? `${Math.round((collected / target) * 100)}%` : '0%';
        return [
          barLabels[index] || `Month ${index + 1}`,
          collected,
          target,
          gap,
          rate,
        ];
      }),
    ];

    const csv = rows
      .map((row) => row.map((cell) => {
        const text = String(cell ?? '');
        return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
      }).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `collection-vs-target-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const openAlert = !!alert && alert.visible !== false && !dismissAlert;

  const recentPaymentRows = recentPayments.map((payment, index) => ([
    payment.unit || `#${index + 1}`,
    payment.owner || '-',
    formatCompactRupees(payment.amount),
    payment.mode || '-',
    <Badge key={`${payment.unit}-${index}`} color={payment.status_color || 'green'} dot>{payment.status || 'Paid'}</Badge>,
    payment.date || '-',
  ]));

  if (isMember) {
    return (
      <div className="fade-up flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center space-y-3 max-w-sm">
          <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
            <Icon name="shield" size={32} className="text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-ink dark:text-white">{t('access_restricted')}</h2>
          <p className="text-muted dark:text-slate-400">{t('restricted_desc')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-up space-y-4">
      {openAlert && !isMember && (
        <Alert type={alert.type === 'danger' ? 'danger' : 'warn'} onClose={() => setDismissAlert(true)}>
          <strong>{t('amc_alert')}</strong> {alert.message}{' '}
          {alert.action_screen ? (
            <span className="underline cursor-pointer font-semibold" onClick={() => onNav(alert.action_screen)}>
              {alert.action_label || t('review_now')}
            </span>
          ) : (
            <span className="font-semibold">{alert.action_label || t('review_now')}</span>
          )}
        </Alert>
      )}

      {error && (
        <Alert type="danger" onClose={() => setError('')}>
          <strong>{t('dashboard_error')}</strong> {error}
        </Alert>
      )}

      {!isMember && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <DashStat
            accent={stats.outstanding_dues?.accent || 'red'}
            icon={stats.outstanding_dues?.icon || 'money'}
            label={t('outstanding_dues')}
            value={loading ? '-' : formatCompactRupees(stats.outstanding_dues?.value)}
            note={stats.outstanding_dues?.note || ''}
            delta={stats.outstanding_dues?.delta || ''}
            deltaUp={stats.outstanding_dues?.delta_up}
            progress={stats.outstanding_dues?.progress ?? 0}
            progressLabel={stats.outstanding_dues?.progress_label || ''}
            chips={stats.outstanding_dues?.chips || []}
            onClick={() => onNav('maintenance')}
          />
          <DashStat
            accent={stats.collected_this_month?.accent || 'green'}
            icon={stats.collected_this_month?.icon || 'trend_up'}
            label={t('collected_this_month')}
            value={loading ? '-' : formatCompactRupees(stats.collected_this_month?.value)}
            note={stats.collected_this_month?.note || ''}
            delta={stats.collected_this_month?.delta || ''}
            deltaUp={stats.collected_this_month?.delta_up}
            progress={stats.collected_this_month?.progress ?? 0}
            progressLabel={stats.collected_this_month?.progress_label || ''}
            chips={stats.collected_this_month?.chips || []}
          />
          <DashStat
            accent={stats.active_amc_contracts?.accent || 'amber'}
            icon={stats.active_amc_contracts?.icon || 'vendors'}
            label={t('active_amc_contracts')}
            value={loading ? '-' : String(stats.active_amc_contracts?.value ?? 0)}
            note={stats.active_amc_contracts?.note || ''}
            delta={stats.active_amc_contracts?.delta || ''}
            deltaUp={stats.active_amc_contracts?.delta_up}
            progress={stats.active_amc_contracts?.progress ?? 0}
            progressLabel={stats.active_amc_contracts?.progress_label || ''}
            chips={stats.active_amc_contracts?.chips || []}
            onClick={() => onNav('vendors')}
          />
          <DashStat
            accent={stats.petty_cash_balance?.accent || 'blue'}
            icon={stats.petty_cash_balance?.icon || 'cash'}
            label={t('petty_cash_balance')}
            value={loading ? '-' : formatCompactRupees(stats.petty_cash_balance?.value)}
            note={stats.petty_cash_balance?.note || ''}
            delta={stats.petty_cash_balance?.delta || ''}
            deltaUp={stats.petty_cash_balance?.delta_up}
            progress={stats.petty_cash_balance?.progress ?? 0}
            progressLabel={stats.petty_cash_balance?.progress_label || ''}
            chips={stats.petty_cash_balance?.chips || []}
          />
        </div>
      )}

      {!isMember && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <Card className="col-span-1 md:col-span-2 lg:col-span-3">
            <SectionHeader
              title={t('collection_vs_target')}
              subtitle={t('monthly_trend')}
              action={<Btn xs variant="ghost" icon="download" onClick={handleExportCollectionChart}>{t('export')}</Btn>}
            />
            {loading ? (
              <div className="py-16 text-center text-muted text-sm">{t('loading_chart')}</div>
            ) : (
              <InteractiveBarChart data={barData} labels={barLabels} />
            )}
          </Card>

          <Card className="col-span-1 lg:col-span-2">
            <SectionHeader title={t('expense_breakdown')} subtitle={`${t('current_month')} · ${charts.expense_breakdown?.center_value || '₹0'} ${t('total')}`} />
            {loading ? (
              <div className="py-16 text-center text-muted text-sm">{t('loading_chart')}</div>
            ) : (
              <DonutChart
                segments={donutSegs}
                centerLabel={t('expenses')}
                centerValue={charts.expense_breakdown?.center_value || '₹0'}
                emptyLabel={t('no_expense_data')}
              />
            )}
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {!isMember && <TrustScoreWidget data={trustScore} t={t} />}

        <div className={`col-span-1 ${isMember ? 'lg:col-span-5 md:col-span-3' : 'md:col-span-2 lg:col-span-3'} space-y-4`}>
          {!isMember && (
            <Card>
              <SectionHeader title={t('recent_payments')} action={<Btn xs icon="chevron_right" onClick={() => onNav('maintenance')}>{t('view_all')}</Btn>} />
              <DataTable
                compact
                cols={[t('col_unit'), t('col_owner'), t('col_amount'), t('col_mode'), t('col_status'), t('col_date')]}
                rows={recentPaymentRows}
                loading={loading}
                emptyMsg={t('no_recent_payments')}
              />
            </Card>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {quickStats.map((stat) => {
              let localizedLabel = stat.label;
              if (stat.label === 'Pending Tasks') localizedLabel = t('pending_tasks');
              if (stat.label === 'Bookings Today') localizedLabel = t('bookings_today');
              if (stat.label === 'Active Notices') localizedLabel = t('active_notices');

              return (
                <Card key={stat.label} className="text-center py-3 px-2 card-hover cursor-pointer" onClick={() => {
                  if (stat.label === 'Pending Tasks') onNav('todos');
                  if (stat.label === 'Bookings Today') onNav('amenities');
                  if (stat.label === 'Active Notices') onNav('notices');
                }}>
                  <div className="text-2xl font-bold text-ink dark:text-slate-100">{loading ? '-' : stat.value}</div>
                  <div className="text-sm text-muted dark:text-slate-300 mt-0.5">{localizedLabel}</div>
                  <div className="mt-1">
                    <Badge color={stat.badge_color || 'blue'} sm>{stat.badge}</Badge>
                  </div>
                </Card>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
